import { DOMParser } from '@xmldom/xmldom'
import * as z from 'zod'

export const iconLibrarySchema = z.object({
  libs: z.object({
    items: z.array(
      z.object({
        _id: z.string(),
        _title: z.string(),
        value: z.string(),
        icons: z.object({
          items: z.array(
            z.object({
              _sys: z.object({
                hash: z.string(),
              }),
              _title: z.string(),
              svg: z.string().optional(),
            })
          ),
        }),
      })
    ),
  }),
})

export class SvgValidator {
  /**
   * Validates if the given string is a valid SVG.
   * @param {string} svgString - The SVG string to validate.
   * @returns {boolean} - Returns true if valid, otherwise false.
   */
  static isValid(svgString: string) {
    if (!this.isWellFormed(svgString)) {
      return false
    }
    if (!this.hasSvgRoot(svgString)) {
      return false
    }
    if (!this.hasRequiredAttributes(svgString)) {
      return false
    }
    if (!this.isContentValid(svgString)) {
      return false
    }
    return true
  }

  /**
   * Checks if the SVG string is well-formed XML.
   * @param {string} svgString - The SVG string to validate.
   * @returns {boolean} - Returns true if well-formed, otherwise false.
   */
  static isWellFormed(svgString: string) {
    // Check for basic string validity first
    if (!svgString || typeof svgString !== 'string') {
      return false
    }

    try {
      const parser = new DOMParser({
        onError: () => false,
      })
      const doc = parser.parseFromString(svgString, 'application/xml')

      const parserErrors = doc.getElementsByTagName('parsererror')

      // If there are parser errors, return false
      if (parserErrors.length > 0) return false

      // Additional check to ensure the document was parsed successfully
      const rootElement = doc.documentElement
      return rootElement !== null && rootElement.tagName !== 'parsererror'
    } catch (e) {
      // Catch any unexpected errors and return false
      return false
    }
  }

  /**
   * Checks if the SVG string contains a valid <svg> root element.
   * @param {string} svgString - The SVG string to validate.
   * @returns {boolean} - Returns true if the <svg> root element exists, otherwise false.
   */
  static hasSvgRoot(svgString: string) {
    const doc = new DOMParser({
      onError: () => false,
    }).parseFromString(svgString, 'application/xml')
    const svgElement = doc.getElementsByTagName('svg')
    return svgElement.length > 0
  }

  /**
   * Checks for required attributes in the <svg> root element.
   * @param {string} svgString - The SVG string to validate.
   * @returns {boolean} - Returns true if required attributes exist, otherwise false.
   */
  static hasRequiredAttributes(svgString: string) {
    const doc = new DOMParser({
      onError: () => false,
    }).parseFromString(svgString, 'application/xml')
    const svgElement = doc.getElementsByTagName('svg')[0]
    if (!svgElement) return false
    return true
  }

  /**
   * Validates the content inside the SVG tag.
   * Rejects invalid HTML tags and <script> tags.
   * @param {string} svgString - The SVG string to validate.
   * @returns {boolean} - Returns true if the content is valid, otherwise false.
   */
  static isContentValid(svgString: string) {
    const doc = new DOMParser({
      onError: () => false,
    }).parseFromString(svgString, 'application/xml')
    const svgElement = doc.getElementsByTagName('svg')[0]
    if (!svgElement) return false

    const invalidTags = ['h1', 'div', 'span', 'script', 'p', 'strong']
    const elements = svgElement.getElementsByTagName('*')

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i]
      if (!el) continue
      const tagName = el.tagName.toLowerCase()
      if (invalidTags.includes(tagName)) {
        return false // Invalid tag found
      }
    }
    return true
  }
}
